import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { Search, ShieldCheck, CheckCircle2, Clock, XCircle, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";

export const Route = createFileRoute("/delete-data/status")({
  component: DeleteDataStatusPage,
  validateSearch: (search: Record<string, unknown>): { email?: string; id?: string } => ({
    email: typeof search.email === "string" ? search.email : undefined,
    id: typeof search.id === "string" ? search.id : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Check Data Deletion Request Status — AdKavi" },
      {
        name: "description",
        content:
          "Look up the status of a data deletion request you submitted to AdKavi using your email and request ID.",
      },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Check Data Deletion Request Status — AdKavi" },
      {
        property: "og:description",
        content: "Look up the status of a data deletion request submitted to AdKavi.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://adkavi.com/delete-data/status" },
    ],
    links: [{ rel: "canonical", href: "https://adkavi.com/delete-data/status" }],
  }),
});

const schema = z.object({
  email: z.string().trim().email("Please enter a valid email").max(255),
  request_id: z.string().trim().min(5, "Enter the request ID from your confirmation").max(64),
});

type LookupResult = {
  request_id: string;
  status: string;
  created_at: string;
  processed_at: string | null;
};

function DeleteDataStatusPage() {
  const { t } = useI18n();
  const search = Route.useSearch();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<LookupResult | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({
    email: search.email ?? "",
    request_id: search.id ?? "",
  });

  const runLookup = async (email: string, request_id: string) => {
    const parsed = schema.safeParse({ email, request_id });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setLoading(true);
    setResult(null);
    setNotFound(false);
    const { data, error } = await supabase.rpc("lookup_deletion_request", {
      _email: parsed.data.email,
      _request_id: parsed.data.request_id,
    });
    setLoading(false);
    if (error) {
      toast.error("Could not look up request. Please try again.");
      return;
    }
    const row = Array.isArray(data) && data.length > 0 ? (data[0] as LookupResult) : null;
    if (!row) {
      setNotFound(true);
      return;
    }
    setResult(row);
  };

  // Auto-lookup if both query params are present
  useEffect(() => {
    if (search.email && search.id) {
      runLookup(search.email, search.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runLookup(form.email, form.request_id);
  };

  const shareUrl =
    typeof window !== "undefined" && result
      ? `${window.location.origin}/delete-data/status?email=${encodeURIComponent(form.email)}&id=${encodeURIComponent(result.request_id)}`
      : "";

  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <main className="min-h-screen bg-background px-6 py-16">
      <div className="max-w-2xl mx-auto">
        <Link to="/delete-data" className="text-sm text-muted-foreground hover:text-foreground">
          {t("delete.status.back")}
        </Link>

        <div className="mt-6 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center">
            <ShieldCheck className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-3xl font-bold">{t("delete.status.title")}</h1>
        </div>

        <p className="mt-4 text-muted-foreground">{t("delete.status.intro")}</p>

        <form
          onSubmit={handleSubmit}
          className="mt-8 space-y-5 rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-6 sm:p-8"
        >
          <div className="space-y-2">
            <Label htmlFor="email">{t("delete.status.email")}</Label>
            <Input
              id="email"
              type="email"
              required
              maxLength={255}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="request_id">{t("delete.status.requestId")}</Label>
            <Input
              id="request_id"
              required
              maxLength={64}
              value={form.request_id}
              onChange={(e) => setForm({ ...form, request_id: e.target.value })}
              placeholder="DR-XXXXXXXXXX"
              className="font-mono"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            <Search className="w-4 h-4 mr-2" />
            {loading ? t("delete.status.loading") : t("delete.status.cta")}
          </Button>
        </form>

        {result && (
          <>
            <StatusResult result={result} t={t} />
            {shareUrl && (
              <div className="mt-4 rounded-xl border border-border/40 bg-muted/40 p-4 text-sm">
                <p className="text-muted-foreground mb-2">{t("delete.status.shareable")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 truncate text-xs bg-background rounded px-2 py-1.5">
                    {shareUrl}
                  </code>
                  <Button size="sm" variant="outline" onClick={copyShare}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {notFound && (
          <div className="mt-6 rounded-2xl border border-border/50 bg-card/50 p-6 text-center">
            <p className="text-sm text-muted-foreground">{t("delete.status.notFound")}</p>
          </div>
        )}
      </div>
    </main>
  );
}

function StatusResult({
  result,
  t,
}: {
  result: LookupResult;
  t: (key: string) => string;
}) {
  const meta = statusMeta(result.status, t);
  return (
    <div className="mt-8 rounded-2xl border border-border/50 bg-card/50 backdrop-blur p-6 sm:p-8">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full ${meta.bg} flex items-center justify-center`}>
          {meta.icon}
        </div>
        <div>
          <h2 className="text-lg font-semibold">{meta.label}</h2>
          <p className="text-sm text-muted-foreground">
            {t("delete.status.request")} {result.request_id}
          </p>
        </div>
      </div>
      <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-muted-foreground">{t("delete.status.submitted")}</dt>
          <dd className="font-medium">{new Date(result.created_at).toLocaleString()}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">
            {result.processed_at ? t("delete.status.processed") : t("delete.status.expectedBy")}
          </dt>
          <dd className="font-medium">
            {result.processed_at
              ? new Date(result.processed_at).toLocaleString()
              : new Date(
                  new Date(result.created_at).getTime() + 30 * 24 * 60 * 60 * 1000
                ).toLocaleDateString()}
          </dd>
        </div>
      </dl>
      <p className="mt-6 text-xs text-muted-foreground">{meta.help}</p>
    </div>
  );
}

function statusMeta(status: string, t: (key: string) => string) {
  switch (status) {
    case "completed":
      return {
        label: t("delete.status.statusCompleted"),
        icon: <CheckCircle2 className="w-5 h-5 text-primary" />,
        bg: "bg-primary/15",
        help: t("delete.status.helpCompleted"),
      };
    case "rejected":
      return {
        label: t("delete.status.statusRejected"),
        icon: <XCircle className="w-5 h-5 text-destructive" />,
        bg: "bg-destructive/15",
        help: t("delete.status.helpRejected"),
      };
    default:
      return {
        label: t("delete.status.statusPending"),
        icon: <Clock className="w-5 h-5 text-primary" />,
        bg: "bg-primary/15",
        help: t("delete.status.helpPending"),
      };
  }
}
